<template>
  <div class="border-b border-solid border-b-main">
    <div class="mb-6">
      <CheckBox v-model="v$.promoted.$model" data-testid="pricing-plan-promoted" class="w-full">
        {{ t('Reviews.product.edit.pricing.promoted.label') }}
      </CheckBox>
      <p class="mt-1 text-label-m text-subtlest">
        {{ t('Reviews.product.edit.pricing.promoted.subline') }}
      </p>
      <div class="mt-4">
        <SlideTransition>
          <div v-if="formData.promoted">
            <FieldWrapper
              for="pricing-plan-promoted-label"
              :label="t('Reviews.product.edit.pricing.promotedLabel.label').toString()"
              data-testid="pricing-plan-promoted-label"
              hint=" "
            >
              <template #default="{ id: inputId }">
                <RichSelectField
                  :id="inputId"
                  v-model="v$.promotedLabel.$model"
                  :placeholder="t('Reviews.product.edit.pricing.promotedLabel.placeholder').toString()"
                >
                  <RichSelectOption
                    v-for="label in promotedLabelOptions"
                    :key="label"
                    :value="label"
                    :label="t(`Reviews.product.edit.pricing.promotedLabel.value.${label}`).toString()"
                  />
                </RichSelectField>
              </template>
            </FieldWrapper>
          </div>
        </SlideTransition>
        <FieldWrapper
          for="pricing-plan-title-icon"
          :label="t('Reviews.product.edit.pricing.titleIcon.label').toString()"
          data-testid="pricing-plan-title-icon"
        >
          <template #default="{ id: inputId }">
            <RichSelectField
              :id="inputId"
              v-model="titleIconModel"
              clearable
              :placeholder="t('Reviews.product.edit.pricing.titleIcon.placeholder').toString()"
            >
              <template #selected="{ selected }">
                <span v-if="selected[0]" class="flex min-w-0 items-center gap-2">
                  <component
                    :is="titleIconRegistry[selected[0].value as TitleIconEnum]"
                    v-if="titleIconRegistry[selected[0].value as TitleIconEnum]"
                    class="lucide-icon-s shrink-0"
                  />
                  <span class="truncate">{{ selected[0].label }}</span>
                </span>
              </template>
              <RichSelectOption
                v-for="icon in titleIconOptions"
                :key="icon"
                :value="icon"
                :label="t(`Reviews.product.edit.pricing.titleIcon.value.${icon}`).toString()"
              >
                <template #prepend>
                  <component :is="titleIconRegistry[icon]" class="lucide-icon-s shrink-0" />
                </template>
              </RichSelectOption>
            </RichSelectField>
          </template>
        </FieldWrapper>
      </div>
    </div>
    <div>
      <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="mt-2">
        <VuelidateField :validation="v$.name[lang]">
          <template #default="{ error, state, countHint }">
            <FieldWrapper :for="`pricing-plan-name-${lang}`" :error="error" :count-hint="countHint">
              <template #label>
                <div class="flex items-center justify-between">
                  <span>{{ t('Reviews.product.edit.pricing.name.label') }}</span>
                  <ChipBadge class="uppercase" size="s" color="purple" rounded>{{ lang }}</ChipBadge>
                </div>
              </template>
              <template #default="{ id }">
                <div class="mt-2">
                  <InputField
                    :id="id"
                    v-model="v$.name[lang].$model"
                    :data-testid="`pricing-plan-name-${lang}`"
                    type="text"
                    :state="state"
                    :maxlength="NAME_MAX_LENGTH"
                    :placeholder="t('Reviews.product.edit.pricing.name.placeholder').toString()"
                  />
                </div>
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
      </div>
    </div>
    <VuelidateField :validation="vPricingOptions$.pricingOption">
      <template #default="{ error, state }">
        <FieldWrapper
          for="pricing-plan-option"
          :label="t('Reviews.product.edit.pricing.pricingOption.label').toString()"
          :error="error"
          data-testid="pricing-plan-option"
          hint=" "
        >
          <template #default="{ id: inputId }">
            <SelectField
              :id="inputId"
              v-model="pricingOption"
              :placeholder="t('Reviews.product.edit.pricing.pricingOption.placeholder').toString()"
              :state="state"
            >
              <option value="price">{{ t('Reviews.product.edit.pricing.pricingOption.value.price') }}</option>
              <option value="text">{{ t('Reviews.product.edit.pricing.pricingOption.value.text') }}</option>
            </SelectField>
          </template>
        </FieldWrapper>
      </template>
    </VuelidateField>
    <template v-if="pricingOption === 'price'">
      <div class="flex">
        <div class="mr-4 w-28">
          <VuelidateField :key="`currency-${currenciesOptions.length}`" :validation="v$.priceCurrency">
            <template #default="{ error, state }">
              <FieldWrapper
                for="pricing-plan-currency"
                :label="t('Reviews.product.edit.pricing.priceCurrency.label').toString()"
                :error="error"
                data-testid="pricing-plan-currency"
                hint=" "
              >
                <template #default="{ id: inputId }">
                  <SelectField
                    :id="inputId"
                    v-model="v$.priceCurrency.$model"
                    :placeholder="t('Reviews.product.edit.pricing.priceCurrency.placeholder').toString()"
                    :state="state"
                  >
                    <option v-for="currency in currenciesOptions" :key="currency.code" :value="currency.code">{{ currency.code }}</option>
                  </SelectField>
                </template>
              </FieldWrapper>
            </template>
          </VuelidateField>
        </div>
        <VuelidateField :validation="v$.priceCents">
          <template #default="{ error, state }">
            <FieldWrapper
              for="pricing-plan-price"
              :label="t('Reviews.product.edit.pricing.priceCents.label').toString()"
              :error="error"
              hint=" "
            >
              <template #default="{ id }">
                <InputField
                  :id="id"
                  v-model.number="v$.priceCents.$model"
                  min="0.01"
                  data-testid="pricing-plan-price"
                  type="number"
                  :state="state"
                />
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
      </div>
      <CheckBox v-model="v$.startingPrice.$model" class="pb-4">
        {{ t('Reviews.product.edit.pricing.startingPrice.label') }}
      </CheckBox>
      <VuelidateField :key="`subscription-modes-${subscriptionModes.length}`" :validation="v$.pricingSubscriptionModeId">
        <template #default="{ error, state }">
          <FieldWrapper
            for="pricing-plan-subscription-mode"
            :label="t('Reviews.product.edit.pricing.pricingSubscriptionMode.label').toString()"
            :error="error"
            data-testid="pricing-plan-subscription-mode"
          >
            <template #default="{ id: inputId }">
              <SelectField
                :id="inputId"
                v-model="v$.pricingSubscriptionModeId.$model"
                :placeholder="t('Reviews.product.edit.pricing.pricingSubscriptionMode.placeholder').toString()"
                :state="state"
              >
                <option v-for="mode in subscriptionModes" :key="mode.id" :value="mode.id">{{ translatedOptionName(mode, locale) }}</option>
              </SelectField>
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </template>
    <VuelidateField
      v-else-if="pricingOption === 'text'"
      :key="`text-option-${pricingTextOptions.length}`"
      :validation="v$.pricingTextOptionId"
    >
      <template #default="{ error, state }">
        <FieldWrapper
          for="pricing-plan-text-mode"
          :label="t('Reviews.product.edit.pricing.pricingTextOption.label').toString()"
          :error="error"
          data-testid="pricing-plan-text-mode"
        >
          <template #default="{ id: inputId }">
            <SelectField
              :id="inputId"
              v-model="v$.pricingTextOptionId.$model"
              :placeholder="t('Reviews.product.edit.pricing.pricingTextOption.placeholder').toString()"
              :state="state"
            >
              <option v-for="option in pricingTextOptions" :key="option.id" :value="option.id">
                {{ translatedOptionName(option, locale) }}
              </option>
            </SelectField>
          </template>
        </FieldWrapper>
      </template>
    </VuelidateField>
    <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="mt-6">
      <VuelidateField :validation="v$.description[lang]">
        <template #default="{ error, state, countHint }">
          <FieldWrapper :for="`pricing-plan-description-${lang}`" :error="error" :count-hint="countHint">
            <template #label>
              <div class="flex items-center justify-between">
                <span>{{ t('Reviews.product.edit.pricing.description.label') }}</span>
                <ChipBadge class="uppercase" size="s" color="purple" rounded>{{ lang }}</ChipBadge>
              </div>
            </template>
            <template #default="{ id }">
              <div class="mt-2">
                <TextareaField
                  :id="id"
                  v-model="v$.description[lang].$model"
                  :data-testid="`pricing-plan-description-${lang}`"
                  :state="state"
                  rows="4"
                />
              </div>
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </div>
    <div class="my-6 flex flex-col gap-6">
      <div v-for="(_, index) in formData.perkList" :key="index" class="relative rounded-sm border border-main px-4 pt-6 pb-2">
        <SortBtnGroup
          class="absolute -top-3.5 right-4"
          :move-up-disabled="index === 0"
          :move-down-disabled="index === formData.perkList.length - 1"
          @destroy="deletePerk(index)"
          @move-up="moveUpPerk(index)"
          @move-down="moveDownPerk(index)"
        />
        <PricingPerkInput
          v-for="lang in ALLOWED_LOCALES"
          :key="lang"
          v-model="formData.perkList[index][lang]"
          :index="index"
          :lang="lang"
          :required="lang === ALLOWED_LOCALES[0]"
        />
      </div>
    </div>
    <button v-if="formData.perkList.length < PERKS_LIMIT" class="btn-link-color-m mb-4" data-testid="add-perk-btn" @click.prevent="addPerk">
      <CirclePlusIcon class="mr-2" />
      {{ t('Reviews.product.edit.pricing.actions.addPerk', formData.perkList.length + 1) }}
    </button>
  </div>
</template>
<script setup lang="ts">
import {
  CheckBox,
  ChipBadge,
  FieldWrapper,
  InputField,
  RichSelectField,
  RichSelectOption,
  SelectField,
  SlideTransition,
  TextareaField,
} from '@ramp106/omrjs-core-ui'
import { CirclePlusIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { computed, nextTick, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Currency, TitleIconEnum } from '@/gql/reviews'
import { PromotedLabelEnum } from '@/gql/reviews'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'
import { buildTranslationFieldsRules } from '@/helpers/validationHelper'
import { required, requiredIf, numeric, maxLength, minLength, minValue } from '@/services/validators'
import SortBtnGroup from '@/ui/SortBtnGroup.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

import type { PricingPlanFormData } from '../types'
import PricingPerkInput from './PricingPerkInput.vue'
import { titleIconRegistry } from './titleIconRegistry'
import { translatedOptionName } from './translatedOptionName'
import type { TranslatedOption } from './types'

const NAME_MIN_LENGTH = 3
const NAME_MAX_LENGTH = 50
const DESCRIPTION_MAX_LENGTH = 200
const PERKS_LIMIT = 50

export interface PricingPlanFormProps {
  value: PricingPlanFormData
  pricingTextOptions: TranslatedOption[]
  subscriptionModes: TranslatedOption[]
  currenciesOptions: Currency[]
  pricingEnabled: boolean
}

const props = defineProps<PricingPlanFormProps>()
const emit = defineEmits(['input', 'promoted-on'])
const { t, locale } = useI18n()

const promotedLabelOptions = Object.values(PromotedLabelEnum)
const titleIconOptions = Object.keys(titleIconRegistry) as TitleIconEnum[]

// RichSelectField emits `undefined` on clear; our form-data type is
// `TitleIconEnum | null`. Bridge the two so the rest of the app stays
// on the null-or-enum invariant.
const titleIconModel = computed<TitleIconEnum | undefined>({
  get: () => formData.titleIcon ?? undefined,
  set: (next) => {
    formData.titleIcon = next ?? null
  },
})

const pricingOption = computed<'text' | 'price' | ''>({
  get() {
    if (typeof props.value.pricingSubscriptionModeId === 'string') {
      return 'price'
    }
    if (typeof props.value.pricingTextOptionId === 'string') {
      return 'text'
    }
    return ''
  },
  set(option) {
    switch (option) {
      case 'price':
        formData.pricingTextOptionId = null
        formData.pricingSubscriptionModeId = ''
        break
      case 'text':
        formData.pricingTextOptionId = ''
        formData.pricingSubscriptionModeId = null
        formData.priceCents = null
        formData.priceCurrency = null
        break
      default:
        formData.pricingTextOptionId = null
        formData.pricingSubscriptionModeId = null
    }
  },
})

const formData = reactive<PricingPlanFormData>({
  ...props.value,
  priceCents: props.value.priceCents ? props.value.priceCents / 100 : undefined,
  perkList: [...props.value.perkList],
})

const rules = computed(() => ({
  description: buildTranslationFieldsRules({ maxLength: maxLength(DESCRIPTION_MAX_LENGTH) }),
  name: {
    ...buildTranslationFieldsRules(
      {
        requiredIf: requiredIf(() => props.pricingEnabled),
        minLength: minLength(NAME_MIN_LENGTH),
        maxLength: maxLength(NAME_MAX_LENGTH),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules(
      {
        minLength: minLength(NAME_MIN_LENGTH),
        maxLength: maxLength(NAME_MAX_LENGTH),
      },
      ALLOWED_LOCALES.slice(1),
    ),
  },
  perkList: {},
  priceCents: { numeric, minValue: minValue(0.01), requiredIf: requiredIf(() => props.pricingEnabled && pricingOption.value === 'price') },
  priceCurrency: { requiredIf: requiredIf(() => props.pricingEnabled && pricingOption.value === 'price') },
  pricingSubscriptionModeId: { requiredIf: requiredIf(() => props.pricingEnabled && pricingOption.value === 'price') },
  pricingTextOptionId: { requiredIf: requiredIf(() => props.pricingEnabled && pricingOption.value === 'text') },
  promoted: {},
  promotedLabel: { requiredIf: requiredIf(() => formData.promoted) },
  startingPrice: {},
  titleIcon: {},
}))

const pricingOptionRules = computed(() => ({
  pricingOption: { required },
}))

const v$ = useVuelidate(rules, formData)
const vPricingOptions$ = useVuelidate(pricingOptionRules, { pricingOption })

function addPerk() {
  formData.perkList.push(ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: { name: '' } }), {}))
}

function deletePerk(index: number) {
  formData.perkList.splice(index, 1)
}

async function moveUpPerk(index: number) {
  if (index > 0) {
    // TODO: Find a better solution.
    const cacheArr = [...formData.perkList]
    // For some reason, without clearing the array and waiting for the next DOM update flush, elements are duplicated instead of replaced.
    formData.perkList = []
    await nextTick()
    const [removed] = cacheArr.splice(index, 1)
    cacheArr.splice(index - 1, 0, removed)
    formData.perkList = [...cacheArr]
  }
}

async function moveDownPerk(index: number) {
  if (index < formData.perkList.length - 1) {
    // TODO: Find a better solution.
    const cacheArr = [...formData.perkList]
    // For some reason, without clearing the array and waiting for the next DOM update flush, elements are duplicated instead of replaced.
    formData.perkList = []
    await nextTick()
    const [removed] = cacheArr.splice(index, 1)
    cacheArr.splice(index + 1, 0, removed)
    formData.perkList = [...cacheArr]
  }
}

watch(
  () => formData,
  () => {
    emit('input', {
      ...formData,
      priceCents: formData.priceCents ? Math.round(formData.priceCents * 100) : undefined,
    })
  },
  { deep: true },
)

watch(
  () => formData.promoted,
  (next, prev) => {
    if (next && !prev) {
      // Default the label so the preview reflects the toggle immediately —
      // otherwise the card stays unchanged until the vendor also picks a
      // label, which reads like the checkbox didn't do anything. The
      // vendor can still pick a different label; Vuelidate requires a
      // non-null value when promoted=true so save remains gated.
      if (formData.promotedLabel == null) {
        formData.promotedLabel = promotedLabelOptions[0]
      }
      emit('promoted-on')
    }
  },
)

// Parent-driven exclusivity: when another plan toggles promoted on, the
// parent unsets it on the siblings. That update lands on `props.value`
// but `formData` is a local reactive copy, so the checkbox would
// otherwise stay visually checked. Sync prop -> local on external
// changes (skip when the values already match to avoid loops).
watch(
  () => props.value.promoted,
  (next) => {
    if (formData.promoted !== next) {
      formData.promoted = next
    }
  },
)
</script>
